import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { PromptBlueprint } from "@/types";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { PlaygroundSessionModeType } from "@/components/Playground/types";

export interface ChatSettingsI {
  injection_variable: string | null; // Placeholder name for history injection (e.g., "chat_history")
  user_input_variable: string | null; // Variable name for user input (e.g., "user_query")
}

export type PlaygroundSessionSource = "raw_request" | "prompt_version";

export interface PlaygroundSessionRequest {
  id: string;
  name: string;
  prompt_blueprint: PromptBlueprint;
  input_variables: Variable[][];
  input_variable_set_names?: string[];
  request_log_ids?: number[];
  snippet_overrides?: Record<string, string>;
  mode?: PlaygroundSessionModeType | "output" | "evaluate"; // NEW
  chat_settings?: ChatSettingsI | null; // NEW - required when mode="chat"
  source?: PlaygroundSessionSource; // Indicates where the session was initialized from
}

export interface PlaygroundSession extends PlaygroundSessionRequest {
  workspace_id: number;
  created_at: string;
  updated_at: string;
}

export interface PlaygroundSessionResponse {
  success: boolean;
  playground_session: PlaygroundSession;
}

const useUpdatePlaygroundSession = (
  userToken: string,
  options?: UseMutationOptions<
    PlaygroundSessionResponse,
    Error,
    { workspaceId: number; session: PlaygroundSessionRequest }
  >,
) =>
  useMutation<
    PlaygroundSessionResponse,
    Error,
    { workspaceId: number; session: PlaygroundSessionRequest }
  >(
    variables =>
      fetch(ENDPOINTS.playground_sessions(variables.workspaceId), {
        method: "PUT",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(variables.session),
      }).then(handleResponse),
    options,
  );

export default useUpdatePlaygroundSession;
